import { DataTableCollapsibleNRow, Dialog, FormMonthRangePicker, FormSectionHeader, FormSelect, Heading, Loader } from "@/components"
import useTrialBalance from "./useTrialBalance"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button, Card, CardContent, Checkbox, Label, RadioGroup, RadioGroupItem, Switch } from "@/components/ui";
import { Clock4, FileText, Filter, Info, Network, RefreshCcw } from "lucide-react";
import { FormProvider } from "react-hook-form";
import { BLOCK, DIST_STATUS, DIST_TYPE, distStatusOption, distTypeOption, GL_MASTER_IDS, MONTH_RANGE, PROPERTY, RECORD_TYPE, recordTypeOption, REPORTING_LEVEL, ZONE } from "./consts";
import { allOption, cn, formatAmount, reportingLevelOptions } from "@/lib/utils";
import { DocumentLineItemDialog } from "@/pages";

const TrialBalance = () => {
  const { breadcrumbData, onAccordionChange, openTabs, form,
    requiredFields, isFinancialInfoLoading, propertyZones, filteredPropertyList,
    isLoadingZones, isLoadingBlocks, isZone, isBlock, isProperty,
    isLoadingProperty, formData, isLoadingReportData, updatedSummaryCards,
    onClear, onSubmit, columns, tableData, isRunClick, currentFinanceYear,
    onDisTypeChange, onDisStatusChange, onRecordTypeChange, onZoneChange, onBlockChange, onPropertyChange, blockList, subColumns,
    openGroupGlDialog, setOpenGroupGlDialog, groupGlList, isDistributed,
    onGlFilterModeChange, openDocumentDialog, setOpenDocumentDialog, selectedDocument, level2Column,
    glOptions, isLoadingGlMaster
   } = useTrialBalance();
  const {errors} = form.formState;

  return (
    <div className="p-4">
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <Heading title={<span>Cost Analytics Trial Balance <span className="text-[12px]">(FY-{currentFinanceYear})</span></span>} breadcrumbData={breadcrumbData} />
      </div>

      {isFinancialInfoLoading || isLoadingZones || isLoadingGlMaster ? <Loader /> :
        <>
          <Accordion
            type="multiple"
            value={openTabs}
            onValueChange={onAccordionChange}
          >
            {/* Invoice Document Management */}
            <AccordionItem
              value={"filter"}
              className="bg-white px-5 rounded-lg mb-[20px] shadow-md"
            >
              <AccordionTrigger className="cursor-pointer hover:no-underline">
                <div className="flex flex-row items-center justify-between w-full">
                  <div className="flex flex-row flex items-center gap-3">
                    <div className="bg-blue-50 h-8 w-8 rounded-sm flex items-center justify-center">
                      <Filter className="h-5 stroke-blue-800"/>
                    </div>
                    <div>
                      <FormSectionHeader className="text-lg" wrapperClassName="mb-0">Filter</FormSectionHeader>
                      <span className="text-gray-500">Refine your trial balance data</span>
                    </div>
                  </div>
                  <div className="flex flex-row items-center gap-4">
                    <Button type="button" variant="outline" onClick={onClear}><RefreshCcw className="h-3 w-3" /> Reset</Button>
                    <div className="w-full flex items-center justify-end gap-2 h-[38px]"
                      onClick={(e) => e.stopPropagation()}>
                      <Label className="text-[14px]">All</Label>
                      <Switch 
                        checked={isDistributed}
                        onCheckedChange={onGlFilterModeChange}
                      />
                      <Label className="text-[14px]">Distributed GL</Label>
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <FormProvider {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[30px] py-4">
                      <FormMonthRangePicker
                        name={MONTH_RANGE}
                        label="Month Range"
                        placeholder="Select Range"
                        control={form.control}
                        onClear={() =>
                          form.setValue(MONTH_RANGE, { from: null, to: null })
                        }
                        isRequired={requiredFields?.includes(`${MONTH_RANGE}.from`) || requiredFields?.includes(`${MONTH_RANGE}.to`)}
                        error={(errors[MONTH_RANGE] as any)?.from?.message || (errors[MONTH_RANGE] as any)?.to?.message}
                        // maxMonthYear={`01/${currentFinanceYear}`}
                        // minMonthYear={`12/${currentFinanceYear}`}
                      />
                      <FormSelect
                        showSearch={false}
                        name={REPORTING_LEVEL}
                        control={form.control}
                        label="Reporting Level"
                        placeholder="Please select"
                        options={reportingLevelOptions}
                        isRequired={requiredFields?.includes(REPORTING_LEVEL)}
                        error={errors[REPORTING_LEVEL]?.message}
                        disabled
                      />
                      {isZone || isBlock || isProperty ? <FormSelect
                        multiSelect
                        name={ZONE}
                        control={form.control}
                        label="Zone"
                        placeholder="Please select"
                        error={errors[ZONE]?.message}
                        isRequired={requiredFields.includes(ZONE)}
                        options={[...allOption, ...(propertyZones?.data || [])?.map((val: any) => ({
                          label: val?.name?.toString(),
                          value: val?.id?.toString(),
                        }))]}
                        onChange={onZoneChange}
                      /> : <></>}
                      {isBlock || isProperty ? <FormSelect
                        multiSelect
                        name={BLOCK}
                        control={form.control}
                        label="Block"
                        placeholder="Please select"
                        error={errors[BLOCK]?.message}
                        isRequired={requiredFields.includes(BLOCK)}
                        options={blockList}
                        onChange={onBlockChange}
                        disabled={isLoadingBlocks}
                      /> : <></>}
                      {isProperty ? <FormSelect
                        multiSelect
                        name={PROPERTY}
                        control={form.control}
                        label="Property"
                        placeholder="Please select"
                        error={errors[PROPERTY]?.message}
                        isRequired={requiredFields.includes(PROPERTY)}
                        onChange={onPropertyChange}
                        options={[...allOption, ...(filteredPropertyList || [])?.map((val: any) => ({
                          value: val?.propertyId?.toString(),
                          label: `${val?.displayId || val?.propertyId || ""} (${val?.name || ""})`,
                        }))]}
                        disabled={isLoadingProperty}
                      /> : <></>}
                      <FormSelect
                        multiSelect
                        name={GL_MASTER_IDS}
                        control={form.control}
                        label="GL Account"
                        placeholder="Please select"
                        options={glOptions}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t-1 border-b-1 py-6">
                      <div className="flex flex-col gap-5 items-center">
                        <span className="flex flex-row items-center gap-1 text-gray-600 font-semibold">
                          <FileText className="h-4" /> Record Type
                        </span>
                        <div>
                          <RadioGroup
                            onValueChange={onRecordTypeChange}
                            value={formData[RECORD_TYPE]}
                            className="ml-1 flex flex-row items-center gap-5">
                            {recordTypeOption?.map((data: any) => 
                              <div className="flex flex-row items-center gap-1">
                                <RadioGroupItem
                                  value={data?.value}
                                  id={data?.value}
                                  className={formData[RECORD_TYPE] == data?.value ? "border-primary" : ""}
                                />
                                <Label htmlFor={data?.value} className="font-normal">{data?.label}</Label>
                              </div>
                            )}
                          </RadioGroup>
                        </div>
                      </div>
                      <div className="flex flex-col gap-5 lg:border-r-1 lg:border-l-1 items-center">
                        <span className="flex flex-row items-center gap-1 text-gray-600 font-semibold">
                          <Clock4 className="h-4" /> Allocation Status
                        </span>
                        <div>
                          <div className="ml-1 flex flex-row items-center gap-5">
                            {distStatusOption?.map((data: any) => 
                              <div className="flex flex-row items-center gap-1">
                                <Checkbox
                                  value={data?.value}
                                  id={data?.value}
                                  onCheckedChange={onDisStatusChange(data?.value)}
                                  className={formData[DIST_STATUS] == data?.value ? "border-primary" : "border-gray-400"}
                                />
                                <Label htmlFor={data?.value} className="font-normal">{data?.label}</Label>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-5 items-center mt-10 lg:mt-auto">
                        <span className="flex flex-row items-center gap-1 text-gray-600 font-semibold">
                          <Network className="h-4" /> Allocation Type
                        </span>
                        <div>
                          <div className="ml-1 flex flex-wrap items-center gap-3">
                            {distTypeOption?.map((data: any) => 
                              <div className="flex flex-row items-center gap-1">
                                <Checkbox
                                  value={data?.value}
                                  id={data?.value}
                                  onCheckedChange={onDisTypeChange(data?.value)}
                                  className={formData[DIST_TYPE] == data?.value ? "border-primary" : "border-gray-400"}
                                />
                                <Label htmlFor={data?.value} className="font-normal">{data?.label}</Label>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 my-3">
                      <div className="lg:col-span-3 bg-blue-50 border-blue-100 border-1 flex flex-row gap-2 p-3 rounded-sm items-center">
                        <Info className="stroke-blue-800 h-5 stroke-2" />
                        <div>
                          <FormSectionHeader wrapperClassName="mb-0">Filter Tip</FormSectionHeader>
                          <span className="text-[12px] text-gray-500">Use these filters to view and analyze your trial balance data across different levels and allocation types.</span>
                        </div>
                      </div>
                      <div className="flex flex-row items-center justify-center gap-2">
                        <Button><Filter className="h-3 w-3" /> Apply Filter</Button>
                      </div>
                    </div>
                  </form>
                </FormProvider>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {
            isRunClick ? isLoadingReportData ? <Loader /> :
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[20px] my-5">
                {updatedSummaryCards?.map((data: any) => {
                  const isDiff = data?.children?.length ? Boolean(data?.children?.[0]?.amt - data?.children?.[1]?.amt) : false;
                  const Icon = data.icon;
                  return (
                    <Card className={cn(data?.className, isDiff ? "bg-red-100" : "")} key={data?.id}>
                      <CardContent className="font-medium  text-[14px] flex flex-col items-center justify-center">
                        <div className="flex flex-row items-center gap-2">
                          <div className={cn("rounded-full flex items-center justify-center h-9 w-9", data.iconWrapper, isDiff ? "bg-red-600" : "")}>
                            <Icon className="stroke-white h-5 w-5" />
                          </div>
                          <label>{data?.title}</label>
                        </div>
                        <div className="border-b border-t border-gray-300 my-2">
                          {data?.children ?
                            <div className="flex flex-row text-[16px] text-black py-3">
                              {data?.children?.map((child: any, index: number) => {
                                return (
                                  <div className={cn("flex flex-col items-center justify-center px-4", index == 0 ? "border-r border-gray-300" : "")}>
                                    <span className="text-gray-500 text-[12px]">{child.label}</span>
                                    <span className="text-[20px]">{formatAmount(child.amt || "0")}</span>
                                  </div>
                                )})
                              }
                            </div>
                          : <div className="h-[70px] text-[20px] text-black py-3 px-4 flex items-center justify-center">{data?.amt || ""}</div>}
                        </div>
                        <label className={cn("text-center text-gray-500")}>{data?.desc}</label>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>

              <DataTableCollapsibleNRow
                data={tableData || []}
                columns={columns || []}
                nestedColumns={[
                  subColumns, // Depth 1 
                  level2Column, // Depth 2 
                  level2Column, // Depth 3 
                  level2Column, // Depth 4 
                  level2Column, // Depth 5
                ]}
                hideHeader={{
                  level1: true,
                  level2: true,
                  level3: true,
                  level4: true,
                  level5: true,
                }}
              />
            </> : <></>
          }
        </>
      }

      <Dialog open={openGroupGlDialog} setOpen={setOpenGroupGlDialog}
        title={
          <div className="text-2xl font-bold text-primary">
            {"View Group GL(s)"}
            <div className="h-1 w-[30%] bg-primary rounded-full" />
          </div>
        }
      >
        <div className="flex flex-col">
          {groupGlList?.length ? groupGlList?.map((data: any) => {
              return (
                <span>{data?.glAccountNumber} - {data?.glName}</span>
              )
            }) : <span>No Data Found</span>
          }
        </div>
      </Dialog>

      {openDocumentDialog && selectedDocument ? (
          <DocumentLineItemDialog
            open={openDocumentDialog}
            setOpen={setOpenDocumentDialog}
            selectedDoc={selectedDocument} 
          />
        ) : <></>}
    </div>
  )
}

export default TrialBalance;
